import { useEffect, useMemo, useState } from 'react';
import { get, query, ref, startAt, endAt, orderByKey } from 'firebase/database';
import { rtdb } from '../firebase';
import { useEstablishmentAuth } from '../context/EstablishmentAuthContext';

function ymdNDaysAgo(n) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

function ymdToday() {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Loads aggregated stats for all CROUS managed by the current admin
 * over the last `days` days. Returns per-CROUS summaries + fleet totals.
 */
export default function useFleetStats(crousIds, days = 7) {
  const { status } = useEstablishmentAuth();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (status !== 'authorized' || !crousIds || crousIds.length === 0) {
      setData({});
      setLoading(false);
      return;
    }
    setLoading(true);
    const from = ymdNDaysAgo(days);
    const to = ymdToday();

    Promise.all(
      crousIds.map((id) =>
        get(query(ref(rtdb, `stats/by_crous/${id}`), orderByKey(), startAt(from), endAt(to)))
          .then((snap) => {
            const rows = [];
            snap.forEach((c) => {
              const v = c.val();
              if (v) rows.push(v);
            });
            return { id, rows };
          })
          .catch(() => ({ id, rows: [] }))
      )
    ).then((results) => {
      const out = {};
      for (const { id, rows } of results) {
        out[id] = rows;
      }
      setData(out);
      setLoading(false);
    });
  }, [crousIds, days, status]);

  const fleet = useMemo(() => {
    let totalPings = 0;
    let totalDays = 0;
    let weightedSum = 0;
    let weightedCount = 0;
    const hourlyAccum = Array.from({ length: 24 }, () => ({ sum: 0, count: 0 }));

    for (const rows of Object.values(data)) {
      for (const r of rows) {
        totalPings += r.pingCount || 0;
        totalDays += 1;
        weightedSum += (r.avgLevel || 0) * (r.pingCount || 0);
        weightedCount += r.pingCount || 0;
        if (r.hourly) {
          for (let h = 0; h < 24; h++) {
            const slot = r.hourly[h] || r.hourly[String(h)];
            if (slot && slot.count > 0) {
              hourlyAccum[h].sum += slot.avg * slot.count;
              hourlyAccum[h].count += slot.count;
            }
          }
        }
      }
    }

    const avgLevel = weightedCount > 0 ? weightedSum / weightedCount : 0;
    const hourly = hourlyAccum.map((h) => (h.count > 0 ? h.sum / h.count : 0));
    const peakHour = hourly.reduce((best, val, i) => (val > hourly[best] ? i : best), 0);

    return { totalPings, totalDays, avgLevel, hourly, peakHour };
  }, [data]);

  return { data, fleet, loading };
}
